"use client";

import { cn } from "@/lib/utils";
import * as React from "react";
import { SubNavigationSidebar } from "./sub-navigation-sidebar";

export interface SubNavigationItem {
  title: string;
  href: string;
  icon?: React.ComponentType<{ className?: string }>;
  badge?: string | number;
  children?: SubNavigationItem[];
}

interface PageWithSubSidebarProps {
  className?: string;
  children: React.ReactNode;
  subNavigation?: SubNavigationItem[];
  searchable?: boolean;
  searchComponent?: React.ReactNode;

  slotStart?: React.ReactNode;
}

interface PageSidebarState {
  isOpen: boolean;
  collapsedGroups: string[];
  groupIsCollapsed: (group: string) => boolean;
  collapseGroup: (group: string) => void;
  toggleCollapsedGroup: (group: string) => void;
  close: () => void;
  open: () => void;
}

interface PageSidebarMenuState {
  isOpen: boolean;
  toggle: () => void;
  close: () => void;
}

const PageSidebarContext = React.createContext<{
  sidebar: PageSidebarState;
  menu: PageSidebarMenuState;
} | null>(null);

export function usePageSidebar() {
  const context = React.useContext(PageSidebarContext);
  if (!context) {
    throw new Error("usePageSidebar must be used within PageWithSubSidebar");
  }
  return context;
}

export function PageWithSubSidebar({
  className,
  children,
  subNavigation,
  searchable = false,
  searchComponent,
  slotStart,
}: PageWithSubSidebarProps) {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth > 765;
    }
    return true;
  });
  const [collapsedGroups, setCollapsedGroups] = React.useState<string[]>([]);

  // Handle window resize
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 765) {
        setMenuOpen(true);
      } else {
        setMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const sidebarState: PageSidebarState = React.useMemo(
    () => ({
      isOpen: sidebarOpen,
      collapsedGroups,
      groupIsCollapsed: (group: string) => collapsedGroups.includes(group),
      collapseGroup: (group: string) => {
        if (!collapsedGroups.includes(group)) {
          setCollapsedGroups((prev) => [...prev, group]);
        }
      },
      toggleCollapsedGroup: (group: string) => {
        setCollapsedGroups((prev) =>
          prev.includes(group)
            ? prev.filter((g) => g !== group)
            : [...prev, group],
        );
      },
      close: () => setSidebarOpen(false),
      open: () => setSidebarOpen(true),
    }),
    [sidebarOpen, collapsedGroups],
  );

  const menuState: PageSidebarMenuState = React.useMemo(
    () => ({
      isOpen: menuOpen,
      toggle: () => setMenuOpen((prev) => !prev),
      close: () => setMenuOpen(false),
    }),
    [menuOpen],
  );

  if (!subNavigation || subNavigation.length === 0) {
    return <>{children}</>;
  }

  return (
    <PageSidebarContext.Provider
      value={{ sidebar: sidebarState, menu: menuState }}
    >
      {slotStart}
      <div className={cn("flex w-full flex-1", className)}>
        {/* Desktop Sidebar */}
        <div
          className={cn(
            "fixed top-(--header-height) bottom-20 left-0 z-20 w-64 shrink-0 overflow-hidden bg-white transition-transform duration-300 md:sticky md:bottom-auto md:h-[calc(100vh-var(--header-height))] md:translate-x-0 md:self-start md:bg-transparent dark:border-zinc-700 dark:bg-zinc-900 md:dark:bg-transparent",
            menuOpen ? "translate-x-0" : "-translate-x-full",
          )}
          onClick={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
        >
          <div className="absolute inset-0 h-full shrink-0 rounded bg-[rgb(241_241_241)] dark:bg-[rgb(19_19_21)]">
            <div
              className="absolute right-0 h-full w-full overflow-auto py-8 pr-2.5 pl-4 md:pt-5"
              style={{ scrollbarGutter: "stable" }}
            >
              {searchable && searchComponent && (
                <div className="mb-4">{searchComponent}</div>
              )}
              <SubNavigationSidebar
                navigation={subNavigation}
                onClick={() => setMenuOpen(false)}
              />
            </div>
          </div>
        </div>

        {/* Mobile Overlay */}
        {menuOpen && (
          <div
            className="fixed inset-0 z-5 bg-zinc-950/50 transition duration-500 md:hidden dark:bg-zinc-950/75"
            onClick={() => setMenuOpen(false)}
          />
        )}

        {/* Content Area */}
        {children}
      </div>
    </PageSidebarContext.Provider>
  );
}
